import type { Chart, ChartDataset, ChartOptions } from "chart.js";

import { InvalidOperationException } from "../../Global/Base/Exceptions";
import { createElement, registerCustomElement } from "../UI.ElementarControls/Utils/CustomElementUtils";
/**
 * Komponenta zobrazující graf pomocí knihovny ChartJS
 * https://www.chartjs.org/docs/latest/
 * @includeToDoc
 */
export class ChartElement extends HTMLElement {
    private _context: CanvasRenderingContext2D | null = null;
    private _chart: Chart | undefined = undefined;
    private _datasets: ChartDataset[] = [];
    private _labels: (string | string[])[] = [];
    private _type: ChartType = ChartType.bar;
    private _chartOption: ChartOptions = {};
    private _chartModule?: Promise<typeof Chart>;

    /**
     * Nastaveni dat
     */
    public get datasets(): ChartDataset[] {
        return this._datasets;
    }
    public set datasets(datasets: ChartDataset[]) {
        this._datasets = datasets;
    }

    /**
     * nastavení popisků
     * */
    public get labels(): Array<string | string[]> {
        return this._labels;
    }
    public set labels(labels: Array<string | string[]>) {
        this._labels = labels;
    }

    /**
     * Typ grafu
     * */
    public get type(): ChartType {
        return this._type;
    }
    public set type(type: ChartType) {
        this._type = type;
    }

    /**
     * možnosti zobrazení grafu
     * */
    public get chartOption(): ChartOptions {
        return this._chartOption;
    }
    public set chartOption(option: ChartOptions) {
        this._chartOption = option;
    }

    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected async _constructor(): Promise<void> {
        // @ts-ignore
        this._chartModule = import("chart.js").then((module) => module.default);

        const canvas = createElement({ type: "canvas" }) as HTMLCanvasElement;
        this._context = canvas.getContext("2d");
        this.appendChild(canvas);
    }

    protected connectedCallback(): void {
        this.showChart();
    }

    protected disconnectedCallback(): void {
        this.clear();
    }

    public clear(): void {
        if (this._chart) {
            try {
                this._chart.destroy();
            } catch {
                //graf mohl být vytvořen, ale z nějakého důvodu nebyl vykreslen, tak destroy spadne do chyby
            }
        }
    }

    public async showChart(): Promise<void> {
        if (!this._chartModule) {
            throw new InvalidOperationException("Chart.js dynamic module wasn't loaded!");
        } else if (!this._context) {
            throw new InvalidOperationException("Cannot create chart. Context is not defined.");
        }

        //pokud neni jedna z hodnot nastavena, nemá vykreslení význam
        if (!this._labels || this._labels.length === 0 || !this._datasets || this._datasets.length === 0) {
            return;
        }

        const Chart = await this._chartModule;

        this.clear();
        this._chart = new Chart(this._context, {
            type: this._type,
            data: {
                labels: this._labels,
                datasets: this._datasets,
            },
            options: this._chartOption,
        });
    }
}

export enum ChartType {
    bar = "bar",
    bubble = "bubble",
    doughnut = "doughnut",
    line = "line",
    pie = "pie",
    polarArea = "polarArea",
    radar = "radar",
    scatter = "scatter",
}

registerCustomElement("tsc-ChartElement", ChartElement);
